import type { NotificationDto, NotificationListDto, NotificationSeverity } from '@an-crm/shared';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  CircleAlert,
  Info,
  type LucideIcon,
  OctagonAlert,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { notificationKeys, notificationsApi, notificationsQuery } from '@/features/notifications/api';
import { friendlyMessage } from '@/lib/api/api-error';
import { cn } from '@/lib/cn';
import { formatDateTimeIst, formatRelative } from '@/lib/format';

const SEVERITY: Record<NotificationSeverity, { icon: LucideIcon; className: string; label: string }> = {
  info: { icon: Info, className: 'bg-info-soft text-info', label: 'Info' },
  warning: { icon: CircleAlert, className: 'bg-warning-soft text-warning', label: 'Warning' },
  critical: { icon: OctagonAlert, className: 'bg-danger-soft text-danger', label: 'Critical' },
};

function badgeText(count: number): string {
  return count > 9 ? '9+' : String(count);
}

export function NotificationsButton({ readOnly = false }: { readOnly?: boolean }) {
  const [open, setOpen] = useState(false);
  const list = useQuery(notificationsQuery);
  const unread = list.data?.unreadCount ?? 0;
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} className="relative">
          <Bell />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                key="badge"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 600, damping: 30 }}
                aria-hidden
                data-testid="notifications-badge"
                className="tabular absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger-strong px-1 text-[10px] leading-none font-semibold text-white ring-2 ring-surface"
              >
                {badgeText(unread)}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          aria-label="Notifications"
          className="z-50 flex max-h-[min(70vh,560px)] w-[min(calc(100vw-1.5rem),380px)] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-e3 data-[state=open]:animate-[pop-in_160ms_var(--ease-out-soft)]"
        >
          <NotificationsPanel
            list={list.data}
            pending={list.isPending}
            error={list.isError ? list.error : null}
            onRetry={() => void list.refetch()}
            readOnly={readOnly}
            onNavigate={() => setOpen(false)}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function NotificationsPanel({
  list,
  pending,
  error,
  onRetry,
  readOnly,
  onNavigate,
}: {
  list: NotificationListDto | undefined;
  pending: boolean;
  error: unknown;
  onRetry: () => void;
  readOnly: boolean;
  onNavigate: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const key = notificationKeys.all;

  const optimistic = async (update: (old: NotificationListDto) => NotificationListDto) => {
    await queryClient.cancelQueries({ queryKey: key });
    const previous = queryClient.getQueryData<NotificationListDto>(key);
    if (previous) queryClient.setQueryData(key, update(previous));
    return { previous };
  };
  const rollback = (
    error: unknown,
    _vars: unknown,
    ctx: { previous: NotificationListDto | undefined } | undefined,
  ) => {
    if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
    toast.error('Couldn’t update notifications', { description: friendlyMessage(error) });
  };

  const markRead = useMutation({
    mutationFn: notificationsApi.markRead,
    onMutate: (id: string) =>
      optimistic((old) => {
        const target = old.items.find((n) => n.id === id);
        if (!target || target.readAt) return old;
        const now = new Date().toISOString();
        return {
          items: old.items.map((n) => (n.id === id ? { ...n, readAt: now } : n)),
          unreadCount: Math.max(0, old.unreadCount - 1),
        };
      }),
    onError: rollback,
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onMutate: () =>
      optimistic((old) => {
        const now = new Date().toISOString();
        return { items: old.items.map((n) => ({ ...n, readAt: n.readAt ?? now })), unreadCount: 0 };
      }),
    onError: rollback,
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const open = (n: NotificationDto) => {
    if (!readOnly && !n.readAt) markRead.mutate(n.id);
    if (!n.link) return;
    onNavigate();
    if (n.link.startsWith('/')) void navigate({ to: n.link });
    else window.open(n.link, '_blank', 'noopener,noreferrer');
  };

  const unread = list?.unreadCount ?? 0;

  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold text-fg">
          Notifications
          {unread > 0 && (
            <span className="tabular rounded-full bg-primary-soft px-1.5 py-px text-[11px] font-semibold text-primary-text">
              {unread} new
            </span>
          )}
        </h2>
        {!readOnly && unread > 0 && (
          <Button variant="ghost" size="sm" className="-mr-2 h-7 px-2" onClick={() => markAll.mutate()}>
            <CheckCheck aria-hidden /> Mark all read
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {pending ? (
          <div className="flex flex-col gap-1 p-2" aria-busy="true" aria-label="Loading notifications">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="flex gap-3 px-2 py-2.5">
                <Skeleton className="size-8 rounded-lg" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-2.5 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={onRetry} compact />
        ) : !list || list.items.length === 0 ? (
          <EmptyState
            icon={BellOff}
            title="You’re all caught up"
            description="Alerts and approvals will appear here."
            compact
          />
        ) : (
          <ul className="flex flex-col p-1.5" aria-label="Recent notifications">
            <AnimatePresence initial={false}>
              {list.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  readOnly={readOnly}
                  onOpen={() => open(n)}
                  onMarkRead={() => markRead.mutate(n.id)}
                />
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </>
  );
}

function NotificationRow({
  n,
  readOnly,
  onOpen,
  onMarkRead,
}: {
  n: NotificationDto;
  readOnly: boolean;
  onOpen: () => void;
  onMarkRead: () => void;
}) {
  const sev = SEVERITY[n.severity];
  const Icon = sev.icon;
  const unread = !n.readAt;
  const content = (
    <>
      <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', sev.className)}>
        <Icon className="size-4" aria-hidden />
        <span className="sr-only">{sev.label}:</span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
        <span className={cn('text-sm text-fg', unread ? 'font-semibold' : 'font-medium text-fg-muted')}>
          {n.title}
          {unread && <span className="sr-only"> (unread)</span>}
        </span>
        {n.body && <span className="line-clamp-2 text-xs text-fg-muted">{n.body}</span>}
        <time
          dateTime={n.createdAt}
          title={formatDateTimeIst(n.createdAt)}
          className="text-[11px] text-fg-subtle"
        >
          {formatRelative(n.createdAt)}
        </time>
      </span>
    </>
  );
  return (
    <motion.li
      layout="position"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'group relative flex items-start gap-1 rounded-lg transition-colors hover:bg-surface-2',
        unread && 'bg-primary-soft/40',
      )}
    >
      {unread && (
        <span
          className="absolute top-1/2 left-0.5 size-1.5 -translate-y-1/2 rounded-full bg-brand"
          aria-hidden
        />
      )}
      {n.link ? (
        <button
          type="button"
          onClick={onOpen}
          className="focus-ring flex flex-1 items-start gap-3 rounded-lg px-2.5 py-2.5"
        >
          {content}
        </button>
      ) : (
        <div className="flex flex-1 items-start gap-3 px-2.5 py-2.5">{content}</div>
      )}
      {unread && !readOnly && (
        <Tooltip content="Mark as read">
          <button
            type="button"
            onClick={onMarkRead}
            aria-label={`Mark “${n.title}” as read`}
            className="focus-ring mt-2 mr-1.5 flex size-7 shrink-0 items-center justify-center rounded-md text-fg-subtle opacity-70 transition-[opacity,background-color] group-hover:opacity-100 hover:bg-surface-3 hover:text-fg focus-visible:opacity-100"
          >
            <Check className="size-3.5" aria-hidden />
          </button>
        </Tooltip>
      )}
    </motion.li>
  );
}
